'use client';

import { useCategoriesQuery } from "@/queries/admin/categories/useCategoriesQuery";
import { LOCALE_HEADER_KEY } from "@/shared/constants/global";
import { Category } from "@/types/category";
import { getImageSrc } from "@/utils/file";
import { Flex, For, HStack, ScrollArea, VStack } from "@chakra-ui/react";
import { useLocale, useTranslations } from "next-intl";
import { FC } from "react";
import { Skeleton, SkeletonCircle } from "../ui/skeleton";
import { CategoryAvatarItem } from "./CategoryAvatarItem";

interface CategoryNavbarProps {
  categorySlug?: Category['slug'];
  isSearchMode?: boolean;
  isOpacityMode?: boolean;
}

const CategoryNavbar: FC<CategoryNavbarProps> = ({ categorySlug, isSearchMode, isOpacityMode = true }) => {
  const t = useTranslations();
  const locale = useLocale();
  const { data: categories, isLoading } = useCategoriesQuery({
    headers: {
      [LOCALE_HEADER_KEY]: locale,
    }
  });

  const SkeletonLoader = () => (
    <HStack gap={{ base: "24px", md: "40px" }} justifyContent={"center"} overflowX={"auto"} mb="16px" mt={"10px"}>
      <For each={[1, 2, 3, 4, 5, 6, 7]}>
        {(_, idx) => (
          <VStack key={idx} gap="5px" align="center" maxW="xs">
            <SkeletonCircle size="70px" css={{ border: '2px solid var(--chakra-colors-gray-400)' }} />
            <Skeleton height="24px" width="70px" />
          </VStack>
        )}
      </For>
    </HStack>
  );

  if (isLoading) {
    return <SkeletonLoader />;
  }

  return (
    <VStack>
      {isSearchMode ? (
        <Flex className="grid-container" gap={{ base: "0", sm: "40px" }} justifyContent={"center"} wrap={"wrap"} maxW="720px">
          <Flex flex={"0 0 calc(20% - 40px)"} justifyContent={"center"}>
            <CategoryAvatarItem
              href={`/shop`}
              label={t('all')}
              imageSrc={"/logo.svg"}
              size="70px"
              isLogoMode
            />
          </Flex>
          <For each={categories ?? []}>
            {(category) => {
              // const isPrevented = (!categorySlug);
              const isActive = (category.slug === categorySlug);
              return (
                <Flex key={category.id} className="grid-item" flex={"0 0 calc(20% - 40px)"} justifyContent={"center"}>
                  <CategoryAvatarItem
                    href={`/shop/${category.slug}`}
                    label={category.name}
                    imageSrc={getImageSrc(category)}
                    isActive={isActive}
                    // isPrevented={isPrevented}
                    isOpacityMode={isOpacityMode}
                    size="70px"
                  />
                </Flex>
              )
            }}
          </For>
        </Flex>
      ) : (
          <>
            <ScrollArea.Root width="100%" size="xs">
              <ScrollArea.Viewport>
                <ScrollArea.Content>
                  <HStack gap={{ base: "24px", md: "40px" }} justifyContent={"center"} overflowX={"auto"} mb="16px" mt={"10px"}>
                    <CategoryAvatarItem
                      href={`/shop`}
                      label={t('all')}
                      imageSrc={"/logo.svg"}
                      size="70px"
                      isLogoMode
                      isActive={!categorySlug}
                      />
                    <For each={categories ?? []}>
                      {(category) => {
                        // const isPrevented = (!categorySlug);
                        const isActive = categorySlug ? (category.slug === categorySlug) : false;
                        return (
                          <CategoryAvatarItem
                            key={category.id}
                            href={`/shop/${category.slug}`}
                            label={category.name}
                            imageSrc={getImageSrc(category)}
                            isActive={isActive}
                            // isPrevented={isPrevented}
                            isOpacityMode={isOpacityMode}
                            size="70px"
                          />
                        )
                      }}
                    </For>
                  </HStack>
                </ScrollArea.Content>
              </ScrollArea.Viewport>
              {/* <ScrollArea.Scrollbar orientation="horizontal" /> */}
              <ScrollArea.Corner />
            </ScrollArea.Root>

          </>
      )}
    </VStack>
  )
}

export default CategoryNavbar;